import api from '../client';

/**
 * Service pour la gestion des utilisateurs
 */
const userService = {
  /**
   * Récupérer la liste des utilisateurs
   * @param {Object} params - Paramètres (role, status, page, limit)
   * @returns {Promise} Liste des utilisateurs
   */
  getUsers: async (params = {}) => {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer un utilisateur par son ID
   * @param {string} id - ID de l'utilisateur
   * @returns {Promise} Données de l'utilisateur
   */
  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Inviter un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur (email, firstName, lastName, role)
   * @returns {Promise} Utilisateur invité
   */
  inviteUser: async (userData) => {
    try {
      const response = await api.post('/users/invite', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @param {Object} userData - Nouvelles données
   * @returns {Promise} Utilisateur mis à jour
   */
  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @returns {Promise} Confirmation de suppression
   */
  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Changer le rôle d'un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @param {string} role - Nouveau rôle (admin, manager, agent)
   * @returns {Promise} Utilisateur mis à jour
   */
  changeRole: async (id, role) => {
    try {
      const response = await api.patch(`/users/${id}/role`, { role });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Activer/Désactiver un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @param {string} status - Statut (active, inactive)
   * @returns {Promise} Utilisateur mis à jour
   */
  updateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/users/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer le profil de l'utilisateur connecté
   * @returns {Promise} Profil de l'utilisateur
   */
  getProfile: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour le profil de l'utilisateur connecté
   * @param {Object} profileData - Nouvelles données du profil
   * @returns {Promise} Profil mis à jour
   */
  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/auth/me', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Changer le mot de passe
   * @param {Object} passwordData - Données du mot de passe
   * @returns {Promise} Confirmation
   */
  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour l'abonnement
   * @param {string} planId - ID du plan
   * @returns {Promise} Abonnement mis à jour
   */
  updateSubscription: async (planId) => {
    try {
      const response = await api.put('/users/subscription', { planId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Acheter des crédits
   * @param {number} amount - Montant des crédits
   * @returns {Promise} Crédits achetés
   */
  buyCredits: async (amount) => {
    try {
      const response = await api.post('/users/buy-credits', { amount });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Activer l'authentification 2FA
   * @returns {Promise} Configuration 2FA
   */
  enable2FA: async () => {
    try {
      const response = await api.post('/users/2fa/enable');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Désactiver l'authentification 2FA
   * @returns {Promise} Confirmation
   */
  disable2FA: async () => {
    try {
      const response = await api.post('/users/2fa/disable');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Vérifier le code 2FA
   * @param {string} code - Code 2FA
   * @returns {Promise} Confirmation
   */
  verify2FA: async (code) => {
    try {
      const response = await api.post('/users/2fa/verify', { code });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les sessions actives
   * @returns {Promise} Liste des sessions
   */
  getActiveSessions: async () => {
    try {
      const response = await api.get('/users/sessions');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Révoquer une session
   * @param {string} sessionId - ID de la session
   * @returns {Promise} Confirmation
   */
  revokeSession: async (sessionId) => {
    try {
      const response = await api.delete(`/users/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Révoquer toutes les sessions sauf l'actuelle
   * @returns {Promise} Confirmation
   */
  revokeAllSessions: async () => {
    try {
      const response = await api.post('/users/sessions/revoke-all');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour les préférences
   * @param {Object} preferences - Préférences utilisateur
   * @returns {Promise} Préférences mises à jour
   */
  updatePreferences: async (preferences) => {
    try {
      const response = await api.put('/users/preferences', preferences);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour les notifications
   * @param {Object} notifications - Paramètres de notification
   * @returns {Promise} Notifications mises à jour
   */
  updateNotifications: async (notifications) => {
    try {
      const response = await api.put('/users/notifications', notifications);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Exporter les données utilisateur
   * @returns {Promise} Données exportées
   */
  exportData: async () => {
    try {
      const response = await api.get('/users/export');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer le compte utilisateur
   * @returns {Promise} Confirmation
   */
  deleteAccount: async () => {
    try {
      const response = await api.delete('/users/account');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Télécharger l'avatar
   * @param {File} file - Fichier image
   * @returns {Promise} URL de l'avatar
   */
  uploadAvatar: async (file) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await api.post('/users/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer l'avatar
   * @returns {Promise} Confirmation
   */
  removeAvatar: async () => {
    try {
      const response = await api.delete('/users/avatar');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtenir les statistiques utilisateur
   * @returns {Promise} Statistiques
   */
  getStats: async () => {
    try {
      const response = await api.get('/users/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour le thème
   * @param {string} theme - Thème (light, dark, system)
   * @returns {Promise} Thème mis à jour
   */
  updateTheme: async (theme) => {
    try {
      const response = await api.put('/users/theme', { theme });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour la langue
   * @param {string} language - Code langue
   * @returns {Promise} Langue mise à jour
   */
  updateLanguage: async (language) => {
    try {
      const response = await api.put('/users/language', { language });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Déconnexion
   * @returns {Promise} Confirmation de déconnexion
   */
  logout: async () => {
    try {
      const response = await api.post('/users/logout');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default userService;
